/** Duomi 平台：历史轮次缺 size/aspect 时的稳定回退比例 */
export const IMAGE_GEN_META_DUOMI_PLATFORM = "duomi";

/** 生图参数快照（时间线 / 任务 options 对齐字段） */
export type ImageGenParamsSnapshot = {
  size?: string;
  width?: number;
  height?: number;
  n?: number;
  quality?: string;
  format?: string;
  style?: string;
  aspect_ratio?: string;
};

/** 构建生图参数展示文案时的最小输入 */
export type ImageGenMetaInput = {
  platform?: string | null;
  modelLabel?: string | null;
  modelId?: string | null;
  aspectLabel?: string | null;
  params?: ImageGenParamsSnapshot | null;
  /** 已生成结果张数 */
  itemCount?: number;
  /** 待发 / 骨架阶段预期张数（优先 params.n） */
  expectedImageCount?: number;
  status?: string | null;
  pendingSubmitDeadline?: number | null;
};

function gcdWh(a: number, b: number): number {
  return b === 0 ? a : gcdWh(b, a % b);
}

function hasSizeField(
  params: ImageGenParamsSnapshot | null | undefined
): params is ImageGenParamsSnapshot & { size: string } {
  return (
    params != null &&
    typeof params === "object" &&
    "size" in params &&
    typeof params.size === "string"
  );
}

/** API `size` 为纯「宽:高」比例串（如 2:3）时归一化 */
function ratioTokenFromColonSize(size: string): string | null {
  const m = size.trim().match(/^(\d+)\s*[:：]\s*(\d+)$/i);
  if (!m) return null;
  const w = parseInt(m[1]!, 10);
  const h = parseInt(m[2]!, 10);
  if (w <= 0 || h <= 0) return null;
  const g = gcdWh(w, h);
  return `${w / g}:${h / g}`.toLowerCase();
}

/** aspectLabel 常以「2:3 竖图」等形式开头，提取前缀比例 */
function ratioTokenFromAspectPrefix(aspect: string): string | null {
  const m = aspect.trim().match(/^(\d+)\s*[:：]\s*(\d+)/);
  if (!m) return null;
  const w = parseInt(m[1]!, 10);
  const h = parseInt(m[2]!, 10);
  if (w <= 0 || h <= 0) return null;
  const g = gcdWh(w, h);
  return `${w / g}:${h / g}`.toLowerCase();
}

/** 历史数据可能把 apiSize（如 3840x2160）误写入 aspectLabel */
function looksLikePixelDimensions(text: string): boolean {
  return /^\d+\s*[x×]\s*\d+$/i.test(text.trim());
}

function formatAspectMeta(aspect: string): string {
  const ratioToken = ratioTokenFromAspectPrefix(aspect);
  return ratioToken ?? aspect;
}

function normalizeAxis(text: string): string {
  return text.replace(/[×✕]/g, "x");
}

function resolveExpectedImageCount(round: ImageGenMetaInput): number {
  const fromParams = round.params?.n;
  if (typeof fromParams === "number" && fromParams >= 1) {
    return Math.min(10, Math.floor(fromParams));
  }
  if (typeof round.expectedImageCount === "number" && round.expectedImageCount >= 1) {
    return Math.min(10, Math.floor(round.expectedImageCount));
  }
  return 1;
}

/**
 * 时间线 / 任务详情等场景：模型 · 比例/尺寸（二选一）· 质量 · 格式 · 张数。
 * 用户端与管理端共用，避免各写一套导致展示不一致。
 *
 * 画幅展示优先级（只选其一）：
 *   1. params.aspect_ratio（后端直接下发的比例参数）
 *   2. aspectLabel（前端控件的比例标签，含「16:9 横图」这种）
 *   3. params.width/height（用 gcd 计算比例，历史数据兜底）
 *   4. params.size（像素值，如 1024x1024）
 *   5. 都没有 → 显示「自动」或尺寸自动
 */
export function buildImageGenMetaItems(round: ImageGenMetaInput): string[] {
  const result: string[] = [];

  const model = round.modelLabel?.trim() || round.modelId?.trim();
  if (model) result.push(model);

  // --- 画幅展示（比例/像素尺寸，二选一）---
  const aspectRatioParam = round.params?.aspect_ratio?.trim();
  const aspectLabel = round.aspectLabel?.trim() ?? "";
  const rawSize = hasSizeField(round.params) ? round.params.size.trim() : "";
  const width = round.params?.width;
  const height = round.params?.height;

  const isAutoAspect = (s: string) => {
    const lc = s.toLowerCase();
    return lc === "" || lc === "自动" || lc === "auto";
  };

  let frameShown = false;

  // 1. params.aspect_ratio 优先（后端直接返回的比例参数）
  if (aspectRatioParam && !isAutoAspect(aspectRatioParam)) {
    const ratioToken = ratioTokenFromAspectPrefix(aspectRatioParam) ?? aspectRatioParam;
    result.push(ratioToken);
    frameShown = true;
  }

  // 2. aspectLabel（前端控件的比例标签）
  if (!frameShown && aspectLabel && !isAutoAspect(aspectLabel)) {
    // aspectLabel 有时会被误写入像素尺寸（如 "1024x1024"）
    if (looksLikePixelDimensions(aspectLabel)) {
      result.push(`尺寸 ${normalizeAxis(aspectLabel)}`);
    } else {
      result.push(formatAspectMeta(aspectLabel));
    }
    frameShown = true;
  }

  // 3. width/height → gcd 算比例（历史数据兜底）
  if (!frameShown && width != null && height != null && width > 0 && height > 0) {
    const g = gcdWh(width, height);
    result.push(`${width / g}:${height / g}`);
    frameShown = true;
  }

  // 4. params.size（像素尺寸，如 1024x1024）
  if (!frameShown && rawSize && rawSize !== "auto") {
    if (looksLikePixelDimensions(rawSize)) {
      result.push(`尺寸 ${normalizeAxis(rawSize)}`);
      frameShown = true;
    } else if (ratioTokenFromColonSize(rawSize)) {
      // size 本身就是比例串（如 "16:9"），按比例处理
      result.push(ratioTokenFromColonSize(rawSize)!);
      frameShown = true;
    } else {
      result.push(rawSize);
      frameShown = true;
    }
  }

  // 5. 都没有或 auto → 显示"未知"（移除 Duomi 专属 1:1 兜底，所有平台统一）
  if (!frameShown) {
    result.push("未知");
  }

  // --- 质量 ---
  const quality = round.params?.quality?.trim();
  if (quality && quality !== "auto") {
    result.push(`质量 ${quality}`);
  }

  // --- 格式 ---
  const format = round.params?.format?.trim();
  if (format) {
    result.push(format.toUpperCase());
  }

  // --- 风格 ---
  const style = round.params?.style?.trim();
  if (style) {
    result.push(`风格 ${style}`);
  }

  // --- 张数 ---
  const itemCount = round.itemCount ?? 0;
  if (itemCount > 0) {
    result.push(`${itemCount}张`);
  } else if (round.status === "PENDING" || round.pendingSubmitDeadline != null) {
    const n = resolveExpectedImageCount(round);
    result.push(`${n}张`);
  } else {
    const n = round.params?.n;
    if (typeof n === "number" && n >= 1) {
      result.push(`${n}张`);
    }
  }

  return result;
}

/** 将生图参数片段拼成单行展示文案 */
export function formatImageGenMetaLine(
  round: ImageGenMetaInput,
  separator = " / "
): string {
  const parts = buildImageGenMetaItems(round);
  return parts.length ? parts.join(separator) : "";
}

/** 时间线轮次（web state / admin 时间线 JSON）共通字段 */
export type TimelineRoundMetaSource = ImageGenMetaInput & {
  items?: ReadonlyArray<unknown> | null;
};

/** 从时间线轮次构建生图参数展示片段 */
export function buildImageGenMetaFromTimelineRound(
  round: TimelineRoundMetaSource
): string[] {
  return buildImageGenMetaItems({
    platform: round.platform,
    modelLabel: round.modelLabel,
    modelId: round.modelId,
    aspectLabel: round.aspectLabel,
    params: round.params,
    itemCount: round.items?.length ?? round.itemCount ?? 0,
    expectedImageCount: round.expectedImageCount ?? round.params?.n,
    status: round.status,
    pendingSubmitDeadline: round.pendingSubmitDeadline
  });
}

/** 漫画整页快照 / 作品设置等仅有 apiSize 的场景 */
export type ApiSnapshotMetaSource = {
  platform?: string | null;
  modelLabel?: string | null;
  modelId?: string | null;
  /** 尺寸选项 value（apiSize） */
  apiSize?: string | null;
  /** 人类可读尺寸标签（若有则作为 aspectLabel） */
  sizeLabel?: string | null;
  aspectRatio?: string | null;
  /** 单次请求张数 */
  count?: number | null;
  /** 追加标签（如「超分」） */
  extras?: string[];
};

export function buildImageGenMetaFromApiSnapshot(
  source: ApiSnapshotMetaSource
): string[] {
  const n =
    typeof source.count === "number" && source.count >= 1
      ? source.count
      : undefined;
  const parts = buildImageGenMetaItems({
    platform: source.platform,
    modelLabel: source.modelLabel,
    modelId: source.modelId,
    aspectLabel: source.sizeLabel,
    params: {
      size: source.apiSize?.trim() || undefined,
      aspect_ratio: source.aspectRatio?.trim() || undefined,
      n
    },
    itemCount: n
  });
  for (const extra of source.extras ?? []) {
    const text = extra.trim();
    if (text) parts.push(text);
  }
  return parts;
}

/** 解析创作任务 options / 请求体中的生图参数字段 */
export function normalizeImageGenParamsSnapshot(
  raw: unknown
): ImageGenParamsSnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const snapshot: ImageGenParamsSnapshot = {};
  if (typeof o.size === "string") snapshot.size = o.size;
  if (typeof o.width === "number") snapshot.width = o.width;
  if (typeof o.height === "number") snapshot.height = o.height;
  if (typeof o.n === "number") snapshot.n = o.n;
  else if (typeof o.n === "string") {
    const parsed = parseInt(o.n, 10);
    if (!Number.isNaN(parsed)) snapshot.n = parsed;
  }
  for (const key of ["quality", "format", "style", "aspect_ratio"] as const) {
    if (typeof o[key] === "string") snapshot[key] = o[key];
  }
  return Object.keys(snapshot).length ? snapshot : null;
}

/** 创作任务 options JSON + 模型上下文 → 生图参数展示片段 */
export function buildImageGenMetaFromOptionsJson(
  optionsJson: string | null | undefined,
  context?: Omit<ImageGenMetaInput, "params">
): string[] {
  let params: ImageGenParamsSnapshot | null = null;
  if (optionsJson?.trim()) {
    try {
      params = normalizeImageGenParamsSnapshot(JSON.parse(optionsJson.trim()));
    } catch {
      params = null;
    }
  }
  if (!params && !context) return [];
  return buildImageGenMetaItems({ ...context, params });
}
